import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useEffect } from 'react';

import { useGetPaymentBreakdown } from '@/api/endpoints/rent/useGetPaymentBreakdown';

interface PaymentLinkCardProps {
  title: string;
  description: string;
  fee: string;
  feeDescription: string;
  url: string;
  buttonText?: string;
  chipText?: string;
  chipColor?: 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary';
  amount?: number;
  isLoading?: boolean;
  feeAmount?: string;
}

// Format currency helper
const formatCurrency = (amount: number, isInCents = true): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
    .format(amount / (isInCents ? 100 : 1))
    .replace('$', '');

const PaymentLinkCard: FC<PaymentLinkCardProps> = ({
  title,
  description,
  fee,
  feeDescription,
  url,
  buttonText = 'Open Link',
  chipText,
  chipColor = 'success',
  amount,
  isLoading = false,
  feeAmount,
}) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flexGrow: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        {chipText && (
          <Chip
            label={chipText}
            color={chipColor}
            size="small"
            sx={{ textTransform: 'uppercase' }}
          />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Fee: {fee}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {feeDescription}
        </Typography>
        {feeAmount && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
            Actual Fee: ${feeAmount}
          </Typography>
        )}
      </Box>

      {amount && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Rent Amount:</strong> ${formatCurrency(amount)}/month
          </Typography>
        </Stack>
      )}
    </CardContent>
    <CardActions>
      <Button
        size="small"
        color="secondary"
        variant="contained"
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
        fullWidth
        disabled={isLoading}
      >
        {buttonText}
      </Button>
    </CardActions>
  </Card>
);

interface PaymentLinksContainerProps {
  achUrl: string;
  cardUrl: string;
  baseAmount?: number;
  onRentUpdated?: () => void;
  unitId: number;
}

export const PaymentLinksContainer: FC<PaymentLinksContainerProps> = ({
  achUrl,
  cardUrl,
  baseAmount,
  onRentUpdated,
  unitId,
}) => {
  const { refetch } = useGetPaymentBreakdown(unitId, !!unitId);

  useEffect(() => {
    if (onRentUpdated) {
      refetch();
    }
  }, [baseAmount, onRentUpdated, refetch]);

  // Get fee amounts from the payment breakdown if available
  // const achFeeAmount = paymentBreakdown?.breakdown?.potentialProcessingFees?.achDirectDebit?.total
  //   ? formatCurrency(paymentBreakdown.breakdown.potentialProcessingFees.achDirectDebit.total, false)
  //   : undefined;

  // const cardFeeAmount = paymentBreakdown?.breakdown?.potentialProcessingFees?.card?.domestic
  //   ? formatCurrency(paymentBreakdown.breakdown.potentialProcessingFees.card.domestic, false)
  //   : undefined;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Box sx={{ width: { xs: '100%', md: '50%' } }}>
        <PaymentLinkCard
          title="Bank Account Payment (ACH)"
          description="ACH Direct Debit offers the lowest processing fees and is the recommended payment method."
          // fee="0.8% fee, $5 max"
          fee="0"
          // feeDescription="Connect your bank account to set up automatic recurring payments with minimal fees."
          feeDescription=""
          url={achUrl}
          buttonText="Preview Tenant ACH Payment Link"
          chipText=""
          chipColor="success"
          amount={baseAmount}
          // feeAmount={achFeeAmount}
        />
      </Box>
      {/* <Box sx={{ width: { xs: '100%', md: '50%' } }}>
        <PaymentLinkCard
          title="Card Payment"
          description="Pay with credit or debit card. Higher processing fees apply."
          fee="2.9% + $0.30 fee"
          feeDescription="Standard card payment processing fees will be applied to each transaction."
          url={cardUrl}
          buttonText="Preview Tenant Credit Card Payment Link"
          chipText="Higher Fee"
          chipColor="warning"
          amount={baseAmount}
          feeAmount={cardFeeAmount}
        />
      </Box> */}
    </Stack>
  );
};

export default PaymentLinkCard;
